import { useFormikContext } from "formik";
import _ from "lodash";
import { useEffect, useRef } from "react";

interface FormObserverProps {
  prefectureByZipCode: any;
  initialPostalCode: string;
  daysInMonth: number[];
  setDaysInMonth: React.Dispatch<React.SetStateAction<number[]>>
}

export const FormObserver: React.FC<FormObserverProps> = ({
  prefectureByZipCode,
  initialPostalCode,
  daysInMonth,
  setDaysInMonth
}) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const lastAutoFilledAddress = useRef<string>("");
  const isInitialMount = useRef(true);
  const previousDobMonth = useRef(values.DobMonth);
  const previousDobYear = useRef(values.DobYear);

  useEffect(() => {
    // Skip on initial mount to avoid clearing user data
    if (isInitialMount.current) {
      isInitialMount.current = false;
      if (values.Address) {
        lastAutoFilledAddress.current = values.Address;
      }
      return;
    }

    if (values.PostalCode?.length === 7 && prefectureByZipCode !== null) {
      const autoAddress =
        prefectureByZipCode.Address1 +
        prefectureByZipCode.Address2 +
        prefectureByZipCode.Address3;

      // Only update if address matches last auto-filled or is empty
      if (
        values.Address === lastAutoFilledAddress.current ||
        values.Address === "" ||
        values.Address === autoAddress
      ) {
        setFieldValue("Address", autoAddress);
        lastAutoFilledAddress.current = autoAddress;
      }

      setFieldValue("PrefectureName", prefectureByZipCode.Address1);
    }

    // Clear fields when postal code is invalid
    if (
      prefectureByZipCode === null &&
      initialPostalCode !== values.PostalCode
    ) {
      setFieldValue("PrefectureName", "");
      setFieldValue("Address", "");
      lastAutoFilledAddress.current = "";
    }

    // Clear when postal code is removed
    if (!values.PostalCode || values.PostalCode.length < 7) {
      setFieldValue("PrefectureName", "");
      setFieldValue("Address", "");
      lastAutoFilledAddress.current = "";
    }
  }, [values.PostalCode, prefectureByZipCode, initialPostalCode, setFieldValue]);

  useEffect(() => {
      const monthChanged = previousDobMonth.current !== values.DobMonth;
      const yearChanged = previousDobYear.current !== values.DobYear;

      if (!monthChanged && !yearChanged) {
        return;
      }

      previousDobMonth.current = values.DobMonth;
      previousDobYear.current = values.DobYear;

      if (values.DobYear && values.DobMonth) {
        const daysCount = new Date(
          Number(values.DobYear),
          Number(values.DobMonth),
          0
        ).getDate();
        const daysArray = Array.from({ length: daysCount }, (_, i) => i + 1);

        if (!_.isEqual(daysArray, daysInMonth)) {
          setDaysInMonth(daysArray);
        }
      }
    }, [values.DobMonth, values.DobYear]);

  return null;
};